(() => {
  const node = document.getElementById('apprved-search-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  const cache = new Map();
  let sequence = 0;
  class LiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[name="q"]');
      if (!this.input || form.dataset.predictiveReady) return;
      form.dataset.predictiveReady = 'true';
      form.classList.add('av-predictive');
      this.id = 'av-suggestions-' + (++sequence);
      this.panel = document.createElement('div');
      this.panel.className = 'av-predictive-panel';
      this.panel.hidden = true;
      this.status = document.createElement('span');
      this.status.className = 'av-predictive-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      form.append(this.panel, this.status);
      for (const [key, value] of Object.entries({
        role: 'combobox', 'aria-autocomplete': 'list', 'aria-haspopup': 'listbox',
        'aria-expanded': 'false', 'aria-controls': this.id, autocomplete: 'off', spellcheck: 'false'
      })) this.input.setAttribute(key, value);
      this.version = 0;
      this.active = -1;
      this.options = [];
      const field = document.createElement('span');
      field.className = 'av-search-field';
      this.input.before(field);
      field.append(this.input);
      this.clearButton = document.createElement('button');
      this.clearButton.type = 'button';
      this.clearButton.className = 'av-search-clear';
      this.clearButton.setAttribute('aria-label', config.clear);
      this.clearButton.textContent = '×';
      this.clearButton.hidden = !this.input.value.length;
      field.append(this.clearButton);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.close(true);
      });
      this.input.addEventListener('input', event => {
        this.clearButton.hidden = !this.input.value.length;
        if (!event.isComposing) this.schedule();
      });
      this.input.addEventListener('focus', () => {
        if (!this.ignoreFocus && this.input.value.trim()) this.schedule(0);
      });
      form.addEventListener('keydown', event => this.keydown(event));
      form.addEventListener('submit', () => this.close());
      this.panel.addEventListener('click', event => {
        if (event.target.closest('[data-av-suggestions-close]')) this.close(true);
      });
      this.panel.addEventListener('pointerover', event => {
        const option = event.target.closest('[role="option"]');
        if (option && this.options.includes(option)) this.select(this.options.indexOf(option), false);
      });
      document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) this.close(); });
      document.addEventListener('focusin', event => { if (!form.contains(event.target)) this.close(); });
      const resize = () => this.resize();
      window.addEventListener('resize', resize, { passive: true });
      window.visualViewport?.addEventListener('resize', resize, { passive: true });
      window.visualViewport?.addEventListener('scroll', resize, { passive: true });
    }
    schedule(delay = 180) {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      const version = ++this.version;
      const query = this.input.value.trim();
      this.select(-1, false);
      if (!query) {
        this.close();
        this.panel.replaceChildren();
        return;
      }
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = config.loading;
      this.timer = window.setTimeout(() => this.search(query, version), delay);
    }
    async search(query, version) {
      const key = query.toLocaleLowerCase();
      let timeout;
      try {
        let html = cache.get(key);
        if (html === undefined) {
          const controller = new AbortController();
          this.controller = controller;
          timeout = window.setTimeout(() => controller.abort(), 6000);
          const endpoint = new URL(config.url, window.location.origin);
          endpoint.search = new URLSearchParams({
            q: query, 'resources[type]': 'product', 'resources[limit]': '10',
            'resources[options][unavailable_products]': 'last',
            section_id: 'apprved-predictive-search'
          }).toString();
          const response = await fetch(endpoint, {
            signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' }
          });
          if (!response.ok) throw new Error('Search unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          html = doc.querySelector('[data-av-suggestions-content]')?.outerHTML || '';
          if (cache.size >= 24) cache.delete(cache.keys().next().value);
          cache.set(key, html);
        }
        if (version !== this.version || query !== this.input.value.trim()) return;
        this.render(html);
      } catch {
        if (version === this.version) this.close();
      } finally {
        window.clearTimeout(timeout);
        if (version === this.version) this.form.removeAttribute('aria-busy');
      }
    }
    render(html) {
      this.panel.innerHTML = html;
      const list = this.panel.querySelector('[role="listbox"]');
      this.options = Array.from(this.panel.querySelectorAll('[role="option"]'));
      if (!list || !this.options.length) {
        this.close(false, false);
        this.status.textContent = config.empty;
        return;
      }
      const normalize = value => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’']/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
      const query = normalize(this.input.value);
      const tokens = query.split(' ');
      const precise = this.options.filter(option => {
        const title = normalize(option.querySelector('.av-predictive-name')?.textContent || '');
        const words = title.split(' ');
        return tokens.every(token => words.some(word => word.startsWith(token))) || title.replaceAll(' ', '').includes(query.replaceAll(' ', ''));
      });
      const heading = this.panel.querySelector('[data-av-suggestions-heading]');
      const label = precise.length ? heading?.dataset.matchLabel : heading?.dataset.relatedLabel;
      if (heading && label) heading.textContent = label;
      if (label) list.setAttribute('aria-label', label);
      const shown = (precise.length ? precise : this.options).slice(0, 6);
      this.options.forEach(option => { if (!shown.includes(option)) option.remove(); });
      this.options = shown;
      list.id = this.id;
      this.options.forEach((option, index) => {
        option.id = this.id + '-' + index;
        option.setAttribute('aria-selected', 'false');
      });
      this.active = -1;
      this.panel.hidden = false;
      this.form.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = config.updated;
      this.resize();
    }
    resize() {
      if (this.panel.hidden) return;
      if (window.matchMedia('(max-width: 700px)').matches) {
        const viewport = window.visualViewport;
        const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        const available = Math.max(0, bottom - this.panel.getBoundingClientRect().top - 8);
        this.panel.style.maxHeight = Math.floor(Math.min(620, available)) + 'px';
      } else this.panel.style.removeProperty('max-height');
    }
    select(index, scroll = true) {
      this.options.forEach((option, position) => option.setAttribute('aria-selected', String(position === index)));
      this.active = index;
      if (index >= 0 && this.options[index]) {
        this.input.setAttribute('aria-activedescendant', this.options[index].id);
        if (scroll) this.options[index].scrollIntoView({ block: 'nearest' });
      } else this.input.removeAttribute('aria-activedescendant');
    }
    keydown(event) {
      if (event.key === 'Escape' && (!this.panel.hidden || this.form.getAttribute('aria-busy') === 'true')) {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.target !== this.input) return;
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !this.panel.hidden && this.options.length) {
        event.preventDefault();
        if (this.form.getAttribute('aria-busy') === 'true') return;
        const count = this.options.length;
        const next = event.key === 'ArrowDown' ? (this.active + 1) % count : (this.active < 0 ? count - 1 : (this.active - 1 + count) % count);
        this.select(next);
      } else if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0 && this.form.getAttribute('aria-busy') !== 'true') {
        event.preventDefault();
        this.options[this.active]?.querySelector('a')?.click();
      }
    }
    close(restoreFocus = false, cancel = true) {
      if (cancel) {
        ++this.version;
        window.clearTimeout(this.timer);
        this.controller?.abort();
      }
      this.panel.hidden = true;
      this.form.classList.remove('is-open');
      this.form.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      this.select(-1, false);
      this.status.textContent = '';
      if (restoreFocus) {
        this.ignoreFocus = true;
        this.input.focus({ preventScroll: true });
        this.ignoreFocus = false;
      }
    }
  }
  const initialize = () => document.querySelectorAll('form[data-av-live-search]').forEach(form => new LiveSearch(form));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();